class PubSub {

    /**
     * Instantiate a PubSub object.
     * @constructor
     */
    constructor() {
        this.subscriptions_ = {};
    };

    /**
     * Subscribe the specified function to events of the specified name.
     * If this function is called while the event is emitting the callback will not receive that event, only the next.
     * @param {string} event_name Name of event to subscribe to.
     * @param {function(*)} callback Function to call. Take a single argument which is the object accompanying the event.
     * @return {function(*)} A reference to the callback which can be given to the unsubscribe function.
     */
    subscribe(event_name, callback) {
        if (this.subscriptions_[event_name] === undefined) {
            //We are not currently handling this event so initialize
            this.subscriptions_[event_name] = {
                subscribers: [callback], //For holding subscribers
                to_subscribe: [],         //For holding new subscribers added while emitting
                to_unsubscribe: [],         //For holding functions to be unsubscribed while emitting
                event_queue: []          //For preventing events being emitted out of order
            };
        } else if (this.subscriptions_[event_name].event_queue.length > 0) {
            //We are currently emitting this event so just enqueue the callback
            //It will be added to the subscribers list when this round of emitting is done
            this.subscriptions_[event_name].to_subscribe.push(callback);
        } else {
            //Not currently emitting so just add to subscribers list
            this.subscriptions_[event_name].subscribers.push(callback);
        }

        return callback;
    };

    /**
     * Subscribe the specified function to events of the specified name but unsubscribe it after having received the event once.
     * If this function is called while the event is emitting the callback will not receive that event, only the next.
     * @param {string} event_name Name of event to subscribe to.
     * @param {function(*)} callback Function to call. Take a single argument which is the object accompanying the event.
     * @return {function(*)} A reference to the callback which can be given to the unsubscribe function.
     */
    subscribeOnce(event_name, callback) {
        const func = function (obj) {
                this.unsubscribe(event_name, func);
                callback(obj);
            }.bind(this);
        return this.subscribe(event_name, func);
    };

    /**
     * Remove the specified callback from receiving events by the specified name.
     * If the event is currently emitting the callback will still receive that event and only after be removed from the list.
     * @param {string} event_name Name of event to remove listener from.
     * @param {function(*)} callback Function that was returned by either the 'subscribe' or 'subscribeOnce' methods.
     */
    unsubscribe(event_name, callback) {
        const event_handler = this.subscriptions_[event_name];
        let idx;
        if (event_handler) {
            //We have handlers for this event
            if ((idx = event_handler.subscribers.indexOf(callback)) >= 0) {
                //The callback is subscribed to this event
                if (event_handler.event_queue.length) {
                    //We are currently emitting this event
                    //so wait till after
                    event_handler.to_unsubscribe.push(callback);
                } else {
                    //Just remove
                    event_handler.subscribers.splice(idx, 1);
                    if (event_handler.subscribers.length === 0) {
                        delete this.subscriptions_[event_name];
                    }
                }
            }
        }
    };

    /**
     * Publish an event to all functions that have subscribed to events of the specified name.
     * Pass the specified object (which can be undefined or null) to all subscribers.
     * If the event is currently emitting this new event will be queued and only emitted after the currently emitting event has done.
     * @param {string} event_name Name of event to publish
     * @param {Object=} object Optional object to pass to all subscribers of this event.
     * @return {Number} The number of subscribers to this event.
     */
    publish(event_name, object) {
        const event_handler = this.subscriptions_[event_name];
        let i, idx, count = 0;

        if (event_handler) {
            //We are handling this event
            event_handler.event_queue.push(object);
            count = event_handler.subscribers.length;
            if (event_handler.event_queue.length === 1) {
                //Only one event in queue so let's emit it (and everything being added while emitting).
                while (event_handler.event_queue.length > 0) {
                    //Get current event object
                    object = event_handler.event_queue[0];

                    //Call all current handlers
                    for (i = 0; i < event_handler.subscribers.length; i++) {
                        event_handler.subscribers[i](object);
                    }

                    //Add new subscribers
                    while (event_handler.to_subscribe.length > 0) {
                        event_handler.subscribers.push(event_handler.to_subscribe.pop());
                    }

                    //Remove new unsubscribers
                    while (event_handler.to_unsubscribe.length > 0) {
                        if ((idx = event_handler.subscribers.indexOf(event_handler.to_unsubscribe.pop())) >= 0) {
                            event_handler.subscribers.splice(idx, 1);
                        }
                    }

                    //Now event has been handled so remove it
                    event_handler.event_queue.shift();
                }

                //If we have no more subscribers
                if (event_handler.subscribers.length === 0) {
                    delete this.subscriptions_[event_name];
                }
            }
        }
        return count;
    };
}